import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./ReferenceInvoiceConsole.css";

interface PatientLike {
  patient_id: string | number;
  name: string;
}

interface InvoiceItemInput {
  description: string;
  quantity: number;
  unit_price: number;
}

interface InvoiceRow {
  id: number;
  invoice_no?: string;
  invoice_date?: string;
  total_amount?: number;
  discount?: number;
  final_amount?: number;
  paid_total?: number;
  balance_due?: number;
  status?: string;
  notes?: string;
  [key: string]: unknown;
}

interface InvoicePaymentRow {
  id: number;
  kind?: string;
  amount?: number;
  method?: string;
  notes?: string;
  created_at?: string;
  [key: string]: unknown;
}

const emptyItem = (): InvoiceItemInput => ({ description: "", quantity: 1, unit_price: 0 });

export default function ReferenceInvoiceConsole({ patient }: { patient: PatientLike }) {
  const { showToast } = useToast();
  const patientId = String(patient.patient_id);

  const [items, setItems] = useState<InvoiceItemInput[]>([emptyItem()]);
  const [discount, setDiscount] = useState<number>(0);
  const [notes, setNotes] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState<{ id: number; invoice_no: string } | null>(null);

  const [history, setHistory] = useState<InvoiceRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [paymentModal, setPaymentModal] = useState<{
    invoiceId: number;
    invoiceNo?: string;
    payments: InvoicePaymentRow[];
    loading: boolean;
  } | null>(null);

  const [paymentForm, setPaymentForm] = useState<{
    kind: "PAYMENT" | "REFUND";
    amount: string;
    method: string;
    notes: string;
  }>({ kind: "PAYMENT", amount: "", method: "Cash", notes: "" });

  const total = useMemo(() => {
    return items.reduce((sum, it) => sum + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
  }, [items]);

  const finalAmount = useMemo(() => Math.max(0, total - Number(discount || 0)), [total, discount]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = (await apiGet(`/api/manager/invoices/patient/${encodeURIComponent(patientId)}?limit=50`)) as InvoiceRow[];
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      showToast({ message: "Failed to load invoice history.", type: "error" });
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [patientId, showToast]);

  useEffect(() => {
    setCreatedInvoice(null);
    setItems([emptyItem()]);
    setDiscount(0);
    setNotes("");
    void loadHistory();
  }, [patientId, loadHistory]);

  const updateItem = (idx: number, patch: Partial<InvoiceItemInput>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const printInvoice = (invoiceNoOrId: string | number) => {
    const invoice = encodeURIComponent(String(invoiceNoOrId));
    window.open(`/print?type=reference-invoice&invoice=${invoice}`, "_blank", "noopener,noreferrer");
  };

  const openPayments = async (invoice: InvoiceRow) => {
    setPaymentModal({ invoiceId: invoice.id, invoiceNo: invoice.invoice_no, payments: [], loading: true });
    setPaymentForm({ kind: "PAYMENT", amount: "", method: "Cash", notes: "" });
    try {
      const data = (await apiGet(`/api/manager/invoices/${invoice.id}/payments`)) as InvoicePaymentRow[];
      setPaymentModal({ invoiceId: invoice.id, invoiceNo: invoice.invoice_no, payments: Array.isArray(data) ? data : [], loading: false });
    } catch (e: unknown) {
      showToast({ message: (e as Error)?.message || "Failed to load payments", type: "error" });
      setPaymentModal({ invoiceId: invoice.id, invoiceNo: invoice.invoice_no, payments: [], loading: false });
    }
  };

  const submitPayment = async () => {
    if (!paymentModal) return;
    const amount = Number(paymentForm.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      showToast({ message: "Enter a valid amount.", type: "warning" });
      return;
    }
    setPaymentModal((prev) => (prev ? { ...prev, loading: true } : prev));
    try {
      await apiPost(`/api/manager/invoices/${paymentModal.invoiceId}/payments`, {
        kind: paymentForm.kind,
        amount,
        method: paymentForm.method || null,
        notes: paymentForm.notes?.trim() || null,
      });
      showToast({ message: "Payment recorded successfully", type: "success" });
      await loadHistory();
      const data = (await apiGet(`/api/manager/invoices/${paymentModal.invoiceId}/payments`)) as InvoicePaymentRow[];
      setPaymentModal((prev) => prev ? { ...prev, payments: Array.isArray(data) ? data : [], loading: false } : prev);
      setPaymentForm({ kind: "PAYMENT", amount: "", method: paymentForm.method || "Cash", notes: "" });
    } catch (e: unknown) {
      showToast({ message: (e as Error)?.message || "Payment save failed", type: "error" });
      setPaymentModal((prev) => (prev ? { ...prev, loading: false } : prev));
    }
  };

  const create = async () => {
    const cleaned = items
      .map((it) => ({
        description: (it.description || "").trim(),
        quantity: Number(it.quantity || 0),
        unit_price: Number(it.unit_price || 0),
      }))
      .filter((it) => it.description && it.quantity > 0);

    if (cleaned.length === 0) {
      showToast({ message: "Add at least one valid line item.", type: "warning" });
      return;
    }

    setLoading(true);
    setCreatedInvoice(null);
    try {
      const res = (await apiPost("/api/manager/invoices", {
        patient_id: patientId,
        items: cleaned,
        discount: Number(discount || 0),
        notes: notes?.trim() || null,
      })) as { ok?: boolean; id?: number; invoice_no?: string };

      if (!res?.id || !res?.invoice_no) throw new Error("Invoice creation failed");
      setCreatedInvoice({ id: res.id, invoice_no: res.invoice_no });
      showToast({ message: "Reference Invoice created successfully", type: "success" });
      setItems([emptyItem()]);
      setDiscount(0);
      setNotes("");
      await loadHistory();
    } catch (e: unknown) {
      showToast({ message: (e as Error)?.message || "Invoice creation failed", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' as const, outline: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>📄 Formatted Quotation</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            For patient reference only. This does <strong>not</strong> impact clinic revenue reports.
          </p>
        </div>
        {createdInvoice && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ecfdf5', padding: '8px 16px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
            <span style={{ fontSize: '0.85rem', color: '#065f46', fontWeight: 600 }}>Created: {createdInvoice.invoice_no}</span>
            <Button variant="primary" size="sm" onClick={() => printInvoice(createdInvoice.invoice_no)}>🖨️ Print</Button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', alignItems: 'start' }}>
        
        {/* Create Invoice Panel */}
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', color: '#334155' }}>Draft Quotation</h4>
          </div>

          <div style={{ padding: '16px' }}>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#f8fafc', padding: '8px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <div style={{ flex: 2 }}>
                    <input
                      style={inputStyle}
                      value={it.description}
                      onChange={(e) => updateItem(idx, { description: e.target.value })}
                      placeholder="Service / Item Name"
                    />
                  </div>
                  <div style={{ flex: 1, maxWidth: '80px' }}>
                    <input
                      style={inputStyle}
                      type="number"
                      min={0}
                      step={1}
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                      placeholder="Qty"
                    />
                  </div>
                  <div style={{ flex: 1, maxWidth: '100px' }}>
                    <input
                      style={inputStyle}
                      type="number"
                      min={0}
                      step={0.01}
                      value={it.unit_price}
                      onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })}
                      placeholder="Price"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => removeItem(idx)} disabled={items.length <= 1} style={{ padding: '6px', color: '#ef4444' }}>
                    X
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" size="sm" onClick={addItem} style={{ marginBottom: '24px', borderStyle: 'dashed' }}>
              + Add Line Item
            </Button>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem', color: '#475569' }}>
                <span>Subtotal</span>
                <strong style={{ fontSize: '1.1rem', color: '#0f172a' }}>₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem', color: '#475569' }}>
                <span>Discount Amount</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value))}
                  style={{ ...inputStyle, maxWidth: '120px', textAlign: 'right' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <span style={{ fontWeight: 600, color: '#166534' }}>Net Payable</span>
                <strong style={{ fontSize: '1.4rem', color: '#15803d' }}>₹{finalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
              </div>
            </div>

            <div style={{ marginTop: '16px', marginBottom: '24px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '8px' }}>Internal Notes (Optional)</label>
              <textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                placeholder="Remarks visible on printout..." 
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              />
            </div>

            <Button variant="primary" fullWidth size="lg" onClick={create} isLoading={loading}>
              Generate Quotation
            </Button>
          </div>
        </div>

        {/* Invoice History Panel */}
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', color: '#334155' }}>Recent Quotations</h4>
          </div>

          <div style={{ padding: '16px', maxHeight: '600px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>Loading history...</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>No previous quotations found.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {history.map((h) => (
                  <div key={h.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', background: '#fff', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                       <div>
                         <div style={{ fontWeight: 700, color: '#0ea5e9', fontSize: '1rem' }}>{h.invoice_no || `#${h.id}`}</div>
                         <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{h.invoice_date ? new Date(h.invoice_date).toLocaleDateString("en-IN") : "—"}</div>
                       </div>
                       <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>₹{Number(h.final_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                          <span style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 8px', borderRadius: '12px', color: '#475569', fontWeight: 600 }}>{h.status || "DRAFT"}</span>
                       </div>
                    </div>

                    <div style={{ display: 'flex', background: '#f8fafc', padding: '8px 12px', borderRadius: '6px', fontSize: '0.8rem', justifyContent: 'space-between' }}>
                       <span style={{ color: '#16a34a', fontWeight: 600 }}>Paid: ₹{Number(h.paid_total || 0).toLocaleString()}</span>
                       <span style={{ color: '#dc2626', fontWeight: 600 }}>Due: ₹{Number(h.balance_due || 0).toLocaleString()}</span>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <Button variant="outline" size="sm" onClick={() => openPayments(h)}>Payments</Button>
                      <Button variant="secondary" size="sm" onClick={() => printInvoice(h.invoice_no || h.id)}>Print</Button>
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {paymentModal && typeof document !== "undefined" && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setPaymentModal(null)}>
          <div style={{ width: '500px', background: '#fff', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            
            <div style={{ padding: '20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>Ledger Entries</h3>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>Quotation {paymentModal.invoiceNo || `#${paymentModal.invoiceId}`}</div>
              </div>
              <button onClick={() => setPaymentModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select 
                  style={{ ...inputStyle, flex: 1 }} 
                  value={paymentForm.kind} 
                  onChange={(e) => setPaymentForm((p) => ({ ...p, kind: e.target.value as "PAYMENT" | "REFUND" }))} 
                  disabled={paymentModal.loading}
                >
                  <option value="PAYMENT">Payment</option>
                  <option value="REFUND">Refund</option>
                </select>
                <div style={{ position: 'relative', flex: 2 }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>₹</span>
                  <input
                    style={{ ...inputStyle, paddingLeft: '28px' }}
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="Amount"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                    disabled={paymentModal.loading}
                  />
                </div>
              </div>

              <input
                style={inputStyle}
                placeholder="Method (Cash / UPI / Card)"
                value={paymentForm.method}
                onChange={(e) => setPaymentForm((p) => ({ ...p, method: e.target.value }))}
                disabled={paymentModal.loading}
              />
              
              <input
                style={inputStyle}
                placeholder="Optional notes..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))}
                disabled={paymentModal.loading}
              />
              
              <Button variant="primary" onClick={submitPayment} isLoading={paymentModal.loading}>Save Ledger Entry</Button>
            </div>

            <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>Prior Ledger Entries</div>
              {paymentModal.loading && paymentModal.payments.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>Loading entries...</div>
              ) : paymentModal.payments.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem', padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>No entries found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {paymentModal.payments.map((p) => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: p.kind === 'REFUND' ? '#dc2626' : '#166534', background: p.kind === 'REFUND' ? '#fee2e2' : '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>
                              {p.kind || "PAYMENT"}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{p.method || "—"}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                            {p.created_at ? new Date(p.created_at).toLocaleString() : "Date unknown"}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: p.kind === 'REFUND' ? '#dc2626' : '#0f172a' }}>
                        ₹{Number(p.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
